// D304: a lazy code chunk that will not load.
//
// Every build gives the lazy chunks (both flows, the stage, the preload runner, the sea, the
// GLTFLoader…) new hashed names, and a deploy removes the old ones. A tab that loaded the
// previous index.html still asks for the old names and gets a 404, in exactly the words a
// dropped connection uses. Retrying cannot help: the page keeps asking for files that are gone,
// and only a fresh index.html names the files that exist. Until this module the networked
// launcher hid the menu BEFORE asking and never caught the answer, so the player was left on an
// empty page behind an eight-second toast.
//
// Two layers, both decided here so a test can reach them (main.ts is coverage-excluded — D185):
//   1. Vite announces every failed lazy import as a cancelable `vite:preloadError` on window, and
//      throws unless the event is prevented. The FIRST one reloads the page — once per build per
//      tab, and only after that fact is written to sessionStorage. A reload that lands on the same
//      build (a cached shell, a deploy still uploading, a network still down) therefore surfaces
//      the failure instead of reloading again: the loop is impossible by construction, not by a timer.
//   2. A surfaced failure reaches the launcher, which puts the player back on a working menu and
//      says why. Both flows go through ONE launcher because they had drifted: the solo launcher
//      caught a failed import (and blamed the connection), the networked one did not catch at all.

/** Where the reload is recorded: the build that last reloaded this tab. sessionStorage survives the reload and dies with the tab. */
export const CHUNK_RELOAD_KEY = 'kolony-chunk-reload';

/**
 * Each browser's words for a module script that never arrived (a 404 and a dropped connection read
 * the same), plus Vite's own for a chunk's stylesheet. Matched as substrings because the url
 * follows, and case-blind because the browsers disagree on capitals. All three engines' real
 * messages were checked against a production build with the chunk removed.
 */
const CHUNK_LOAD_FAILURE = [
  'failed to fetch dynamically imported module', // Chromium
  'error loading dynamically imported module', // Firefox
  'importing a module script failed', // WebKit
  'unable to preload css', // Vite's preload helper
] as const;

const messageOf = (err: unknown): string => (err instanceof Error ? err.message : String(err));

/** Did this error come from a chunk that never arrived, rather than from code that arrived and threw? */
export function isChunkLoadError(err: unknown): boolean {
  const message = messageOf(err).toLowerCase();
  return CHUNK_LOAD_FAILURE.some((words) => message.includes(words));
}

/** The slice of Storage the guard uses. Either call may throw: blocked site data, a full quota. */
export interface ReloadMarks {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export type ChunkFailureVerdict = 'reload' | 'surface';

/**
 * Reload, or let the failure through to the launcher?
 *
 * Only a chunk that never ARRIVED is a reload's to fix: a module that arrived and threw is a bug
 * that a reload would only repeat. And only once per build per tab, RECORDED before the reload is
 * asked for. A mark that cannot be written, or does not read back, cannot stop the next reload, so
 * a store that throws or swallows writes surfaces every failure rather than risking a loop.
 */
export function chunkFailureVerdict(payload: unknown, build: string, marks: () => ReloadMarks): ChunkFailureVerdict {
  if (!isChunkLoadError(payload)) return 'surface';
  try {
    const store = marks(); // reading `window.sessionStorage` itself throws when site data is blocked
    if (store.getItem(CHUNK_RELOAD_KEY) === build) return 'surface'; // this build already reloaded this tab, and it did not help
    store.setItem(CHUNK_RELOAD_KEY, build);
    return store.getItem(CHUNK_RELOAD_KEY) === build ? 'reload' : 'surface';
  } catch {
    return 'surface';
  }
}

/** What Vite dispatches: `payload` is the failed import's error, and preventing it stops the throw. */
export interface PreloadFailure {
  readonly payload?: unknown;
  preventDefault(): void;
}

export interface ChunkRecovery {
  /** The window's `vite:preloadError` listener. */
  onPreloadError(event: PreloadFailure): void;
  /**
   * True once this page has asked to be reloaded. Nothing on a page that is about to be replaced
   * reports anything: the error toast, the fatal screen and the launcher all read this. The reload
   * cannot be cancelled today because nothing registers a `beforeunload` prompt; if one is ever
   * added, this must learn about a cancelled reload.
   */
  reloading(): boolean;
}

export interface ChunkRecoveryOpts {
  /** Names THIS build. main.ts passes the entry chunk's own url, whose hash moves whenever a chunk it imports is renamed. */
  readonly build: string;
  readonly marks: () => ReloadMarks;
  readonly reload: () => void;
}

export function createChunkRecovery(opts: ChunkRecoveryOpts): ChunkRecovery {
  let reloading = false;
  return {
    onPreloadError(event): void {
      if (!reloading) {
        if (chunkFailureVerdict(event.payload, opts.build, opts.marks) === 'surface') return; // Vite throws, and the launcher tells the player
        try {
          opts.reload();
        } catch {
          return; // nothing will replace this page, so the failure must still reach someone
        }
        reloading = true;
      }
      // Vite throws unless told not to. On a page that is being replaced, by this failure or an
      // earlier one (a launcher asks for three chunks at once), there is nobody left to tell. The
      // import resolves to nothing instead, and its caller's own catch stays quiet (launchFlow).
      event.preventDefault();
    },
    reloading: () => reloading,
  };
}

/** What a player back on the menu is told when part of the game never arrived. */
export const CHUNK_FAILURE_NOTICE = "The game couldn't load — KOLONY may have been updated. Reload the page to play.";

/**
 * The words for a launch that failed. A missing chunk is a deploy or a dropped connection (the
 * browser cannot tell them apart, and a reload fixes both). Anything else is a flow that threw
 * while starting, named as such rather than blamed on the connection.
 */
export function launchFailureText(err: unknown): string {
  return isChunkLoadError(err) ? CHUNK_FAILURE_NOTICE : `Could not start the game (${messageOf(err)})`;
}

/** What a launch needs from the page: the menu, somewhere to mount, and a voice. */
export interface LaunchPorts {
  readonly doc: Document;
  hideMenu(): void;
  /** Back to a WORKING menu: the screen and its music bed (the flow may already have stepped it aside). */
  showMenu(): void;
  /** Tell the player why they are back on the menu. */
  notify(text: string): void;
  /** A reload is already replacing this page (`ChunkRecovery.reloading`). */
  reloading(): boolean;
}

/**
 * Start a flow whose code is a lazy chunk: hide the menu, give the flow a root of its own, load the
 * chunks and start. ANY failure on the way (a chunk that never arrived, a flow that threw while
 * starting) takes the root down and puts the menu back, so the player is never left on an empty
 * page. Settles once the flow has started or the menu is back.
 */
export function launchFlow<M>(ports: LaunchPorts, load: () => Promise<M>, start: (modules: M, root: HTMLElement) => void): Promise<void> {
  ports.hideMenu();
  const root = ports.doc.createElement('div');
  ports.doc.body.appendChild(root);
  return Promise.resolve()
    .then(load)
    .then((modules) => start(modules, root))
    .catch((err: unknown) => {
      root.remove();
      ports.showMenu();
      // When the recovery has stopped Vite throwing, the import RESOLVED to nothing and `start`
      // threw on it. The page is already on its way to a fresh build, so the menu comes back in
      // case the reload never lands, but nothing is said: a notice now would flash and vanish.
      if (!ports.reloading()) ports.notify(launchFailureText(err));
    });
}
